import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SESSION_DURATION_MS } from "../constants/chat";
import {
  expireSessionNow,
  loadOtherSession,
  loadSession,
} from "./sessionManager";
import { chatDelete } from "../api/chatApi";

const SKIP_EXPIRY_PATHS = new Set([
  "/session-expired",
  "/transferred-chat",
  "/no-active-chats",
  "/importuser",
]);

function isSessionEntryPath(pathname: string) {
  return (
    pathname === "/home" ||
    pathname.startsWith("/qr/") ||
    pathname.startsWith("/connect/") ||
    pathname.startsWith("/publicid/connect/") ||
    pathname.startsWith("/c/") ||
    pathname.startsWith("/p/")
  );
}

export function useSessionExpiry() {
  const navigate = useNavigate();
  const location = useLocation();
  const expiredRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (SKIP_EXPIRY_PATHS.has(location.pathname)) return;

    const clearExistingTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const expireNow = () => {
      if (expiredRef.current) return;
      expiredRef.current = true;

      clearExistingTimer();

      const session = loadSession();
      const otherSession = loadOtherSession();

      const accessToken = session?.requestToken;
      const otherUserId = otherSession?.otherUserId;
      const chatId = otherSession?.chatId;

      navigate("/session-expired", { replace: true });

      void (async () => {
        try {
          if (accessToken && otherUserId && chatId) {
            try {
              await chatDelete(otherUserId, accessToken, chatId);
            } catch (error) {
              console.error(
                "Failed to delete chat before session expiry:",
                error,
              );
            }
          }
        } finally {
          await expireSessionNow();
          window.dispatchEvent(new Event("idless:session-updated"));
        }
      })();
    };

    const recomputeAndSchedule = () => {
      if (expiredRef.current) return;

      clearExistingTimer();

      const startRaw = sessionStorage.getItem("sessionStartTime");
      const startTime = parseInt(startRaw || "0", 10);

      const session = loadSession();
      const hasRealSession = !!session?.requestToken;

      /**
       * Important:
       * On direct link open, the page may not have created the session yet.
       * Do not redirect to /session-expired just because sessionStartTime
       * is missing during initial entry.
       */
      if (!startTime) {
        if (hasRealSession && !isSessionEntryPath(location.pathname)) {
          expireNow();
        }

        return;
      }

      const timeLeft = SESSION_DURATION_MS - (Date.now() - startTime);

      if (timeLeft <= 0) {
        expireNow();
        return;
      }

      timerRef.current = window.setTimeout(expireNow, timeLeft);
    };

    recomputeAndSchedule();

    window.addEventListener("idless:session-updated", recomputeAndSchedule);
    window.addEventListener("storage", recomputeAndSchedule);

    return () => {
      clearExistingTimer();
      window.removeEventListener("idless:session-updated", recomputeAndSchedule);
      window.removeEventListener("storage", recomputeAndSchedule);
    };
  }, [navigate, location.pathname]);
}